import React from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { useMutation, useQuery, useLazyQuery } from 'react-apollo'
import { t } from 'src/lib/i18n-react'
import Action from 'src/ui/Action/Action'
import routes from 'src/lib/rails-routes/rails-routes'
import NudgeCard from 'src/ui/NudgeCard/NudgeCard'
import NudgeCardContent from 'src/ui/NudgeCard/subcomponents/NudgeCardContent'
import StudyGoals from 'src/ui/StudyGoals/StudyGoals'
import StudyGoalsPlaceholder from 'src/ui/StudyGoals/StudyGoalsPlaceholder'
import Home from 'src/pages/Home/Home'
import HomePlaceholder from 'src/pages/Home/HomePlaceholder'
import DataFetchError, {
  ERROR_CODE_500,
} from 'src/ui/DataFetchError/DataFetchError'
import NextActivityLiveLesson from 'src/pages/Home/subcomponents/NextActivity/NextActivityLiveLesson'
import NextActivityWelcome from 'src/pages/Home/subcomponents/NextActivity/NextActivityWelcome'
import NextActivitySelfStudy from 'src/pages/Home/subcomponents/NextActivity/NextActivitySelfStudy'
import NextActivities from 'src/ui/NextActivities/NextActivities'
import UpcomingTutoringAppointment from 'src/ui/UpcomingTutoringAppointment/UpcomingTutoringAppointment'

const CANCEL_APPOINTMENT = gql`
  mutation CANCEL_APPOINTMENT($appointment: AppointmentUpdateInputType!) {
    response: modifyAppointmentStatus(appointment: $appointment) {
      appointment {
        startTime
        id
      }
      errors {
        message
      }
    }
  }
`

export const FETCH_MAIN_ACTIVITY_AND_MY_COURSE = gql`
  query FETCH_MAIN_ACTIVITY_AND_MY_COURSE(
    $l1Code: LanguageEnum!
    $l2Code: LanguageEnum!
    $resume: Boolean
  ) {
    myCourse(native: $l1Code, learning: $l2Code) {
      id
    }
    activity(code: $l2Code, resume: $resume) {
      identifier
      name
      heading
      format
      coverImageUrl
      unit {
        id
        name
        label(format: "unit_sequence_number")
        topic: level {
          name
          isQueueable
        }
      }
    }
  }
`

const GET_PROGRESS = gql`
  query GET_PROGRESS($l2Code: LanguageEnum!) {
    currentUser {
      progress(code: $l2Code) {
        weeklyGoals(kind: minutes) {
          completed: progress
          goal: final
          completeFraction: percent
        }
        liveLessonWeeklyGoal: weeklyGoals(kind: live_lesson) {
          completed: progress
          goal: final
          completeFraction: percent
        }
      }
    }
  }
`

const POLL_INTERVAL_SECONDS = 10 * 60 // every 10 minutes
const POLL_INTERVAL = POLL_INTERVAL_SECONDS * 1000

export const FETCH_APPOINTMENTS = gql`
  query FETCH_APPOINTMENTS {
    currentUser {
      upcomingStudentAppointments: upcomingAppointments(
        role: student
        status: confirmed
        startsInMinutes: 1440
        startedSinceMinutes: 10
      ) {
        id
        chatSessionId
        startTime
        endTime
        hasConfirmedTutor
        startsInSeconds
        plannerType
        language {
          code
        }
        plannedUnits {
          id
          gid
          iconUrl
          name
          progress {
            percentOfExercisesSeen
          }
        }
        tutor {
          id
          avatarUrl
          location
          login
          name
          fluentLanguages {
            code
            localizedName
          }
          tutor {
            isFavorite
            countOfCompletedLiveLessons
          }
        }
      }
      upcomingTutorAppointments: upcomingAppointments(
        role: tutor
        status: confirmed
        startsInMinutes: 1440
        startedSinceMinutes: 10
      ) {
        startTime
        unit {
          iconUrl
        }
        student {
          login
          avatarUrl
        }
        language {
          code
        }
      }
    }
  }
`

export const FETCH_RECOMMENDED_ACTIVITIES = gql`
  query FETCH_RECOMMENDED_ACTIVITIES(
    $l2Code: LanguageEnum!
    $excludedActivities: [String!]
  ) {
    recommendedActivities(code: $l2Code, exclude: $excludedActivities) {
      identifier
      heading
      unit {
        name
      }
      format
      coverImageUrl
    }
  }
`

export const FETCH_RECOMMENDED_TOPICS = gql`
  query FETCH_RECOMMENDED_TOPICS($l2Code: LanguageEnum!) {
    currentUser {
      recommendedTopics(code: $l2Code) {
        id
        heading: name
        coverImageUrl
        countOfUnits
      }
    }
  }
`

export const FETCH_CURRENT_UNIT = gql`
  query FETCH_CURRENT_UNIT($l2Code: LanguageEnum!) {
    currentUser {
      currentUnit(code: $l2Code) {
        id
      }
    }
  }
`

const HomeLazy = ({ l1Code, l2Code, onShowSnackbar }) => {
  const [cancelAppointment, { loading: cancelMutationLoading }] = useMutation(
    CANCEL_APPOINTMENT
  )
  const responseUpcomingAppointment = useQuery(FETCH_APPOINTMENTS, {
    pollInterval: POLL_INTERVAL,
    fetchPolicy: 'network-only',
  })

  const responseMainActivityAndMyCourse = useQuery(
    FETCH_MAIN_ACTIVITY_AND_MY_COURSE,
    {
      variables: {
        l1Code,
        l2Code,
      },
      fetchPolicy: 'network-only',
    }
  )

  const responseProgress = useQuery(GET_PROGRESS, {
    variables: {
      l2Code,
    },
  })

  const upcomingTutoringAppointment =
    responseUpcomingAppointment.data?.currentUser
      ?.upcomingTutorAppointments?.[0] || null

  //Note: Unit and chatSessionId can sometimes be null for an appointment
  const upcomingAppointment =
    responseUpcomingAppointment.data?.currentUser
      ?.upcomingStudentAppointments?.[0] || null

  const mainActivity = responseMainActivityAndMyCourse.data?.activity
  const excludedActivities = mainActivity?.identifier
    ? [mainActivity.identifier]
    : []

  // if current_unit is nil, we query recommended topics, if it isn't -- recommended activities
  const [
    fetchRecommendedActivities,
    responseRecommendedActivities,
  ] = useLazyQuery(FETCH_RECOMMENDED_ACTIVITIES, {
    variables: {
      l2Code,
      excludedActivities,
    },
    fetchPolicy: 'network-only',
  })

  const [fetchRecommendedTopics, responseRecommendedTopics] = useLazyQuery(
    FETCH_RECOMMENDED_TOPICS,
    {
      variables: {
        l2Code,
      },
      fetchPolicy: 'network-only',
    }
  )

  const responseCurrentUnit = useQuery(FETCH_CURRENT_UNIT, {
    variables: {
      l2Code,
    },
    onCompleted: (data) => {
      if (data?.currentUser?.currentUnit) {
        fetchRecommendedActivities()
      } else {
        fetchRecommendedTopics()
      }
    },
  })

  const recommendedActivities =
    responseRecommendedActivities?.data?.recommendedActivities

  const recommendedTopics =
    responseRecommendedTopics?.data?.currentUser?.recommendedTopics

  const myCourseStarted =
    responseMainActivityAndMyCourse?.data?.myCourse?.length > 0

  const isQueueEmpty = !responseCurrentUnit?.data?.currentUser?.currentUnit

  const weeklyGoals = responseProgress.data?.currentUser?.progress?.weeklyGoals
  const liveLessonGoals =
    responseProgress.data?.currentUser?.progress?.liveLessonWeeklyGoal

  const handleCancelAppointment = async (reason, appointment) => {
    if (cancelMutationLoading) {
      return
    }

    const errorMessage = t(
      'Error while cancelling live lesson. Please try again later.'
    )
    const successMessage = t('Your live lesson was successfully cancelled')

    try {
      const options = {
        variables: {
          appointment: { id: appointment.id, action: 'CANCEL', reason },
        },
      }
      const mutationResponse = await cancelAppointment(options)

      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        responseUpcomingAppointment.refetch()
        onShowSnackbar({ message: errorMessage, type: 'error' })
      } else {
        responseUpcomingAppointment.refetch()
        onShowSnackbar({ message: successMessage, type: 'info' })
      }
    } catch (error) {
      onShowSnackbar({ message: errorMessage, type: 'error' })
    }
  }

  const handleSkipActivity = () => {
    responseMainActivityAndMyCourse?.refetch({ l2Code, resume: false })
  }

  if (!mainActivity && responseMainActivityAndMyCourse.loading) {
    return <HomePlaceholder />
  }

  if (upcomingAppointment || mainActivity || !myCourseStarted) {
    return (
      <Home
        goalsSection={
          responseProgress.loading ? (
            <StudyGoalsPlaceholder />
          ) : (
            weeklyGoals &&
            liveLessonGoals && (
              <StudyGoals
                subtitle={t("This Week's Goal")}
                linkSection={
                  <Action href={routes.language_goals(l2Code)}>
                    {t('view goals')}
                  </Action>
                }
                weeklyGoals={weeklyGoals}
                liveLessonGoals={liveLessonGoals}
              />
            )
          )
        }
        nudgeCardSection={
          <>
            <NudgeCard name="nudge_card:schedule_live_lesson">
              <NudgeCardContent
                type="schedule"
                nudgeUrl={routes.advanced_schedule(l2Code)}
              />
            </NudgeCard>
            <NudgeCard name="nudge_card:review_live_lesson">
              <NudgeCardContent
                type="review_live_lesson"
                nudgeUrl={routes.advanced_achievements(l2Code)}
              />
            </NudgeCard>
            <NudgeCard name="nudge_card:set_goals">
              <NudgeCardContent
                type="set_goals"
                nudgeUrl={routes.language_goals(l2Code)}
              />
            </NudgeCard>
          </>
        }
        tutoringAppointmentSection={
          upcomingTutoringAppointment && (
            <UpcomingTutoringAppointment {...upcomingTutoringAppointment} />
          )
        }
        nextActivitySection={
          !myCourseStarted ? (
            <NextActivityWelcome />
          ) : upcomingAppointment && upcomingAppointment.chatSessionId ? (
            <NextActivityLiveLesson
              upcomingAppointment={upcomingAppointment}
              onCancelAppointment={handleCancelAppointment}
            />
          ) : (
            <NextActivitySelfStudy
              activity={mainActivity}
              onSkipActivity={handleSkipActivity}
            />
          )
        }
        recommendedActivitiesSection={
          !myCourseStarted ? null : isQueueEmpty ? (
            <NextActivities
              title={t('Add more topics to learn new material')}
              activityCardList={recommendedTopics}
              l2Code={l2Code}
              isQueueEmpty
            />
          ) : (
            <NextActivities
              title={t('Practice with Activities')}
              activityCardList={recommendedActivities}
              l2Code={l2Code}
            />
          )
        }
      />
    )
  }

  return <DataFetchError errorCode={ERROR_CODE_500} />
}

HomeLazy.propTypes = {
  l1Code: PropTypes.string.isRequired,
  l2Code: PropTypes.string.isRequired,
  onShowSnackbar: PropTypes.func,
}

export default HomeLazy
